import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip } from 'recharts';
import { useInvestorAuth } from '../../context/InvestorAuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const STATUS_STYLE = {
  published: { bg: '#eff4ff', color: '#3a6ad9', label: 'Open for bids' },
  bidding:   { bg: '#f0fde4', color: '#111111', label: 'Bidding' },
  under_loi: { bg: '#fffde4', color: '#111111', label: 'Under LOI' },
};

function fmtUpb(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
}
function fmt$(n) {
  const v = Number(n) || 0;
  return v ? `$${Math.round(v).toLocaleString()}` : '—';
}
function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function topNFromObj(obj, n = 6) {
  if (!obj || typeof obj !== 'object') return [];
  return Object.entries(obj)
    .map(([name, value]) => ({ name, value: Number(value) || 0 }))
    .sort((a, b) => b.value - a.value)
    .slice(0, n);
}

export default function MarketPoolDetailInvestor() {
  const { id } = useParams();
  const { token, investor } = useInvestorAuth();
  const [pool, setPool] = useState(null);
  const [loans, setLoans] = useState([]);
  const [myBid, setMyBid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/market/pools/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPool(res.data.pool);
      setLoans(res.data.loans || []);
      setMyBid(res.data.myBid);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load pool.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <Shell><div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Loading…</div></Shell>;
  }
  if (error || !pool) {
    return <Shell><div style={{ padding: 32, textAlign: 'center', color: '#ff082f' }}>{error || 'Pool not found.'}</div></Shell>;
  }

  const statusInfo = STATUS_STYLE[pool.status] || { bg: '#f3f4f6', color: '#374151', label: pool.status };
  const isApproved = investor?.status === 'approved';

  return (
    <Shell>
      <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ marginBottom: 16 }}>
          <Link to="/market/pools" style={{ color: '#3a6ad9', textDecoration: 'none', fontSize: 13 }}>← All pools</Link>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 22 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: '#111827', margin: 0 }}>{pool.poolName}</h1>
            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 14, color: '#6b7280' }}>{pool.poolNumber}</span>
              <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 9999, backgroundColor: statusInfo.bg, color: statusInfo.color, fontSize: 12, fontWeight: 700 }}>
                {statusInfo.label}
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)', gap: 24 }}>
          {/* LEFT — Pool details */}
          <div>
            {pool.isRedacted ? (
              <div style={card}>
                <div style={cardTitle}>Pool Preview</div>
                <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7 }}>
                  Detailed pool data — including total UPB, asking price, and the
                  loan tape — is available to approved investors only. Your account
                  status is currently <strong>{investor?.status || '—'}</strong>.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginTop: 12 }}>
                  <Stat label="Loan Count" value={pool.loanCount || 0} />
                  <Stat label="WA FICO" value={pool.waFico || '—'} />
                  <Stat label="WA Note Rate" value={pool.waNoteRate ? `${Number(pool.waNoteRate).toFixed(2)}%` : '—'} />
                </div>
              </div>
            ) : (
              <>
                <div style={card}>
                  <div style={cardTitle}>Summary</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                    <Stat label="Total UPB" value={fmtUpb(pool.totalUpb)} />
                    <Stat label="Loan Count" value={pool.loanCount || 0} />
                    <Stat label="WA Note Rate" value={pool.waNoteRate ? `${Number(pool.waNoteRate).toFixed(2)}%` : '—'} />
                    <Stat label="WA FICO" value={pool.waFico || '—'} />
                    <Stat label="WA LTV" value={pool.waOriginalLtv ? `${Number(pool.waOriginalLtv).toFixed(0)}%` : '—'} />
                    <Stat label="Avg. Age" value={pool.waLoanAgeMonths ? `${pool.waLoanAgeMonths} mo` : '—'} />
                  </div>
                </div>

                <div style={{ ...card, marginTop: 16 }}>
                  <div style={cardTitle}>Stratification</div>
                  <Strat label="Geographic Distribution" data={topNFromObj(pool.geographicSummary, 6)} />
                  <Strat label="Property Type"           data={topNFromObj(pool.propertyTypeSummary, 6)} />
                  <Strat label="Occupancy"               data={topNFromObj(pool.occupancySummary, 6)} />
                  <Strat label="Doc Type"                data={topNFromObj(pool.docTypeSummary, 6)} />
                </div>

                <div style={{ ...card, marginTop: 16 }}>
                  <div style={cardTitle}>Loan Tape Preview ({Math.min(loans.length, 10)} of {loans.length})</div>
                  {loans.length === 0 ? (
                    <div style={{ padding: 16, color: '#9ca3af', fontSize: 13 }}>No loans available.</div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f9fafb' }}>
                          {['State', 'Type', 'Occ.', 'UPB', 'Rate', 'FICO', 'LTV', 'Age', 'Pay Hist.'].map((h) => (
                            <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {loans.slice(0, 10).map((l) => (
                          <tr key={l.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                            <td style={td}>{l.property_state || '—'}</td>
                            <td style={td}>{l.property_type || '—'}</td>
                            <td style={td}>{l.occupancy || '—'}</td>
                            <td style={td}>{fmt$(l.current_upb)}</td>
                            <td style={td}>{l.note_rate ? `${Number(l.note_rate).toFixed(2)}%` : '—'}</td>
                            <td style={td}>{l.original_fico || '—'}</td>
                            <td style={td}>{l.original_ltv ? `${l.original_ltv}%` : '—'}</td>
                            <td style={td}>{l.loan_age_months ?? '—'}</td>
                            <td style={td}>{l.payment_history || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <div style={{ marginTop: 14, padding: 12, backgroundColor: '#f9fafb', borderRadius: 10, fontSize: 12, color: '#6b7280' }}>
                    Full tape and supporting documents available in the Due Diligence Room after bid acceptance.
                  </div>
                </div>
              </>
            )}
          </div>

          {/* RIGHT — Bid panel (sticky) */}
          <div style={{ position: 'sticky', top: 16, alignSelf: 'start' }}>
            <BidPanel
              pool={pool}
              myBid={myBid}
              isApproved={isApproved}
              token={token}
              onBid={load}
            />
          </div>
        </div>
      </div>
    </Shell>
  );
}

function BidPanel({ pool, myBid, isApproved, token, onBid }) {
  const [bidPricePct, setBidPricePct] = useState('');
  const [bidNotes, setBidNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const calcAmount = useMemo(() => {
    const pct = Number(bidPricePct);
    if (!Number.isFinite(pct) || pct <= 0) return 0;
    return (pct / 100) * (Number(pool.totalUpb) || 0);
  }, [bidPricePct, pool.totalUpb]);

  const handleSubmit = async () => {
    setError('');
    const pct = Number(bidPricePct);
    if (!Number.isFinite(pct) || pct <= 50 || pct > 110) {
      setError('Bid price must be between 50 and 110 (% of UPB).');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/market/pools/${pool.id}/bid`,
        { bidPricePct: pct, bidNotes: bidNotes || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onBid();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit bid.');
    } finally {
      setSubmitting(false);
    }
  };

  // Bidding closed because pool moved past 'bidding'/'published'.
  const poolAcceptingBids = ['published', 'bidding'].includes(pool.status);

  // Header — always visible
  const Header = (
    <div style={{ marginBottom: 14 }}>
      {pool.askingPricePct != null && (
        <div style={{ marginBottom: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Asking Price</span>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#111827' }}>{Number(pool.askingPricePct).toFixed(2)}%</div>
        </div>
      )}
      {pool.bidDeadline && (
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          Bid deadline: <strong style={{ color: '#374151' }}>{fmtDate(pool.bidDeadline)}</strong>
        </div>
      )}
    </div>
  );

  // ─── State 7 — Pending approval ─────────────────────────────
  if (!isApproved) {
    return (
      <div style={card}>
        {Header}
        <div style={{ padding: 14, backgroundColor: '#f9fafb', borderRadius: 10, fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
          Approval required to submit bids. Your account is currently under review.
        </div>
      </div>
    );
  }

  // ─── State 6 — Bid rejected ─────────────────────────────────
  if (myBid && myBid.status === 'rejected') {
    return (
      <div style={card}>
        {Header}
        <div style={cardTitle}>Bid Not Selected</div>
        <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6, marginBottom: 6 }}>
          Thank you for your interest. This pool is no longer available to you.
          Browse other pools for new opportunities.
        </p>
        {myBid.rejectionReason && (
          <div style={{ marginTop: 10, padding: 12, backgroundColor: '#ffe6e9', border: '1px solid #ffd6db', borderRadius: 10, fontSize: 12, color: '#ff082f' }}>
            <strong>Reason:</strong> {myBid.rejectionReason}
          </div>
        )}
        <Link to="/market/pools" style={{ display: 'block', marginTop: 14, textAlign: 'center', color: '#3a6ad9', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
          Browse Pools →
        </Link>
      </div>
    );
  }

  // ─── State 5 — DD Open ─────────────────────────────────────
  if (myBid && myBid.status === 'dd_open') {
    return (
      <div style={card}>
        {Header}
        <div style={{ ...cardTitle, color: '#acf44e' }}>Due Diligence Room Open</div>
        <Link
          to={`/market/bids/${myBid.id}/dd-room`}
          style={{ display: 'block', textAlign: 'center', backgroundColor: '#acf44e', color: '#fff', padding: '12px 18px', borderRadius: 100, fontSize: 14, fontWeight: 700, textDecoration: 'none' }}
        >
          Enter DD Room →
        </Link>
      </div>
    );
  }

  // ─── State 4 — LOI signed, DD not yet open ─────────────────
  if (myBid && myBid.status === 'loi_signed') {
    return (
      <div style={card}>
        {Header}
        <div style={cardTitle}>LOI Executed ✓</div>
        <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
          Doorly is preparing your Due Diligence Room. You will be notified when access is granted.
        </p>
      </div>
    );
  }

  // ─── State 3 — Accepted, LOI pending ────────────────────────
  if (myBid && myBid.status === 'loi_pending') {
    return (
      <div style={card}>
        {Header}
        <div style={{ ...cardTitle, color: '#acf44e' }}>Your Bid Was Accepted!</div>
        <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
          A Letter of Intent has been sent to your email via BoldSign. Please sign to proceed to Due Diligence.
        </p>
        <p style={{ fontSize: 12, color: '#6b7280', marginTop: 10 }}>
          DD Room will open after both parties execute the LOI.
        </p>
      </div>
    );
  }

  // ─── State 2 — Bid submitted ───────────────────────────────
  if (myBid && myBid.status === 'submitted') {
    return (
      <div style={card}>
        {Header}
        <div style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 9999, backgroundColor: '#f0fde4', color: '#111111', fontSize: 12, fontWeight: 700, marginBottom: 12 }}>
          Bid Submitted
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Your bid</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>
            {Number(myBid.bidPricePct).toFixed(2)}% = {fmt$(myBid.bidAmount)}
          </div>
        </div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          Submitted {fmtDate(myBid.submittedAt)} · Pending Doorly review.
        </div>
      </div>
    );
  }

  // ─── State 8 — Pool no longer accepting bids ───────────────
  if (!poolAcceptingBids) {
    return (
      <div style={card}>
        {Header}
        <div style={{ padding: 14, backgroundColor: '#f9fafb', borderRadius: 10, fontSize: 13, color: '#374151' }}>
          This pool is no longer accepting bids.
        </div>
      </div>
    );
  }

  // ─── State 1 — Approved + NDA signed, no active bid yet ────
  return (
    <div style={card}>
      {Header}
      <div style={cardTitle}>Submit Your Bid</div>
      {error && (
        <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 8, backgroundColor: '#fff5f6', border: '1px solid #ffd6db', color: '#ff082f', fontSize: 12 }}>
          {error}
        </div>
      )}
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
        Bid Price (% of UPB)
      </label>
      <input
        type="number"
        step="0.01"
        min={50.01}
        max={110}
        value={bidPricePct}
        onChange={(e) => setBidPricePct(e.target.value)}
        placeholder="e.g. 92.50"
        style={inputStyle}
      />
      {bidPricePct && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
          ≈ {fmt$(calcAmount)} of {fmtUpb(pool.totalUpb)} UPB
        </div>
      )}
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, marginTop: 14 }}>
        Notes (optional)
      </label>
      <textarea
        rows={3}
        value={bidNotes}
        onChange={(e) => setBidNotes(e.target.value)}
        style={{ ...inputStyle, height: 'auto', padding: 10, fontFamily: 'inherit', resize: 'vertical' }}
        placeholder="Any conditions or notes for our review team"
      />
      <button
        onClick={handleSubmit}
        disabled={submitting}
        style={{
          width: '100%',
          marginTop: 14,
          backgroundColor: '#3a6ad9',
          color: '#fff',
          border: 'none',
          borderRadius: 100,
          padding: '12px 18px',
          fontSize: 14,
          fontWeight: 700,
          cursor: submitting ? 'not-allowed' : 'pointer',
          opacity: submitting ? 0.6 : 1,
        }}
      >
        {submitting ? 'Submitting…' : 'Submit Bid'}
      </button>
      <p style={{ marginTop: 12, fontSize: 11, color: '#9ca3af', lineHeight: 1.5 }}>
        Bids are non-binding until a Letter of Intent is fully executed between both parties.
      </p>
    </div>
  );
}

function Strat({ label, data }) {
  if (!data || data.length === 0) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
      <ResponsiveContainer width="100%" height={Math.max(140, data.length * 26)}>
        <BarChart layout="vertical" data={data} margin={{ left: 12, right: 16, top: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
          <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#374151' }} axisLine={false} tickLine={false} width={80} />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
          <Bar dataKey="value" fill="#3a6ad9" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ backgroundColor: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 10, padding: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: '#111827', marginTop: 2 }}>{value}</div>
    </div>
  );
}

function Shell({ children }) {
  const { investor, investorLogout } = useInvestorAuth();
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <header style={{ backgroundColor: '#fff', borderBottom: '1px solid #e5e7eb', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/market/dashboard" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
          <img src="/doorly-market-logo.png" alt="Doorly Market" style={{ height: '32px', width: 'auto', display: 'block' }} />
        </Link>
        <nav style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
          <Link to="/market/dashboard" style={{ fontSize: 13, fontWeight: 600, color: '#374151', textDecoration: 'none' }}>Dashboard</Link>
          <Link to="/market/pools" style={{ fontSize: 13, fontWeight: 600, color: '#374151', textDecoration: 'none' }}>Pools</Link>
          <Link to="/market/bids" style={{ fontSize: 13, fontWeight: 600, color: '#374151', textDecoration: 'none' }}>My Bids</Link>
          <span style={{ fontSize: 13, color: '#6b7280' }}>
            {investor?.firstName} {investor?.lastName}
          </span>
          <button onClick={investorLogout} style={{ backgroundColor: 'transparent', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer', color: '#374151' }}>
            Sign out
          </button>
        </nav>
      </header>
      {children}
    </div>
  );
}

const card      = { backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 20 };
const cardTitle = { fontSize: 15, fontWeight: 800, color: '#111827', marginBottom: 12 };
const inputStyle = { width: '100%', height: 44, padding: '0 12px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 14, boxSizing: 'border-box' };
const td = { padding: '8px 10px', fontSize: 12, color: '#374151' };
